<script setup lang="ts" generic="Item, U extends string">
import { sortDown, sortUp } from '@/components/icons/icons';
import { I18nKey } from '@/utils/typings.ts';
import { computed, reactive, watch } from 'vue';
import { VDataTable, VDataTableServer } from 'vuetify/components';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import { ref, type Ref } from 'vue';
import { DataTableItem } from './types';
import { useTranslation } from 'i18next-vue';
import type {
  HeadersSlotProps,
  InternalDataTableHeader,
  OverviewTableProps,
  SelectableItem,
  SlotItem,
  SortItem,
  Group,
} from './types';
import { isEqual } from 'lodash';

const { t } = useTranslation();

const props = withDefaults(defineProps<OverviewTableProps<Item, U>>(), {
  itemsPerPage: -1,
  hideDefaultFooter: () => true,
  itemValue: 'id',
  zebra: false,
  noSpacing: false,
  serverSidePagination: false,
  showItemsCount: true,
  disabled: false,
  itemsLength: undefined,
  mustSort: true,
  defaultSortBy: () => [],
  sortBy: () => [],
  itemsPerPageOptions: () => [
    {
      title: '10',
      value: 10,
    },
    {
      title: '20',
      value: 20,
    },
    {
      title: '50',
      value: 50,
    },
    {
      title: '100',
      value: 100,
    },
  ],
});

const tableComponent = computed(() => (props.serverSidePagination ? VDataTableServer : VDataTable));

const footerProps = {
  itemsPerPageText: t('ItemsPerPage'),
  pageText: t('PageText'),
};

const passdownProps = computed(() => ({
  ...props,
  ...footerProps,
  headers:
    props.headers &&
    indexingColumnHeaders(props.headers as unknown as InternalDataTableHeader[], ref(0)),
})) as any;

const slots = defineSlots<
  {
    [K in U as `item.${K}`]?: (_: SlotItem<Item, U>) => any;
  } & {
    [K in U as `header.${K}`]?: (_: any) => any;
  } & {
    // headers aka VDataTableHeadersSlots
    headers(props: HeadersSlotProps): any;
  } & {
    // rows aka VDataTableRowsSlots
    item(
      props: {
        disabled: boolean;
        index: number;
        item: Item;
        internalItem: DataTableItem<Item, U>;
        isExpanded: (item: DataTableItem<Item, U>) => boolean;
        toggleExpand: (item: DataTableItem<Item, U>) => void;
        isSelected: (items: { value: any; selectable: boolean } | SelectableItem[]) => boolean;
        toggleSelect: (item: { value: any; selectable: boolean }) => void;
      } & { value: any; columns: InternalDataTableHeader[] },
    ): any;
  } & {
    'expanded-row': (props: {
      index: number;
      item: any;
      internalItem: DataTableItem<Item, U>;
      isExpanded: (item: DataTableItem<Item, U>) => boolean;
      toggleExpand: (item: DataTableItem<Item, U>) => void;
      isSelected: (items: { value: any; selectable: boolean } | SelectableItem[]) => boolean;
      toggleSelect: (item: { value: any; selectable: boolean }) => void;
      columns: InternalDataTableHeader[];
    }) => any;
  } & {
    'group-header': (props: {
      index: number;
      item: Group;
      columns: InternalDataTableHeader[];
      isExpanded: (item: DataTableItem<Item, U>) => boolean;
      toggleExpand: (item: DataTableItem<Item, U>) => void;
      isSelected: (items: { value: any; selectable: boolean } | SelectableItem[]) => boolean;
      toggleSelect: (item: { value: any; selectable: boolean }) => void;
      toggleGroup: (group: Group) => void;
      isGroupOpen: (group: Group) => boolean;
    }) => any;
  }
>();

function indexingColumnHeaders(headers: InternalDataTableHeader[], columnIdx: Ref<number>) {
  return headers.map((header: InternalDataTableHeader) => {
    const newHeader = { ...header };

    if (!header.children)
      newHeader.headerProps = header.hidden
        ? undefined
        : {
            columnIdx: columnIdx.value++,
            headerKey: header.title === '' ? header.key : undefined,
          };
    else newHeader.children = indexingColumnHeaders(header.children, columnIdx);

    return newHeader;
  });
}

const emits = defineEmits<{
  'update:expanded': [expanded: string[]];
  'update:options': [options: any];
  'update:sortBy': [sortBy: SortItem[]];
}>();

const sortOptions = reactive({
  mustSort: props.mustSort,
  sortBy: props.sortBy,
});

function updateSortOptions(
  sortBy = props.sortBy,
  mustSort = props.mustSort,
  defaultSortBy = props.defaultSortBy,
) {
  const defaultSortByInUse = isEqual(
    sortBy.map((s) => s.key),
    defaultSortBy.map((s) => s.key),
  );

  sortOptions.mustSort = defaultSortByInUse || mustSort;
  sortOptions.sortBy = sortBy?.length
    ? sortBy
    : defaultSortBy.map((sortItem) => ({ order: 'asc', ...sortItem }));
}

// this way the watcher is not triggered by sortBy update
function handleSortByUpdate(newSortBy: SortItem[]) {
  updateSortOptions(newSortBy);
  emits('update:sortBy', sortOptions.sortBy);
}

watch(
  () => [props.sortBy, props.mustSort, props.defaultSortBy],
  () => updateSortOptions(),
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="overviewTable" :class="{ zebra: zebra, 'no-spacing': noSpacing }">
    <div class="text-end mb-1">
      <v-label v-if="showItemsCount" class="text-primary-lighten-1">
        <template v-if="itemLabel">
          {{ passdownProps.itemsLength || passdownProps.items?.length || 0 }}
          {{
            $t(itemLabel || 'Item', {
              count: passdownProps.itemsLength || passdownProps.items?.length || 0,
            } as unknown as I18nKey)
          }}
        </template>
        <template v-else>&nbsp;</template>
      </v-label>
    </div>

    <component
      :is="tableComponent"
      v-bind="passdownProps"
      :sortAscIcon="sortUp"
      :sortDescIcon="sortDown"
      :mustSort="sortOptions.mustSort"
      :sortBy="sortOptions.sortBy"
      :footerProps="footerProps"
      @update:expanded="emits('update:expanded', $event)"
      @update:options="emits('update:options', $event)"
      @update:sortBy="handleSortByUpdate($event)"
    >
      <template v-for="(_, name) in slots" #[name]="slotData">
        <slot :name="name as keyof typeof slots & string" v-bind="{ ...slotData, disabled }" />
      </template>

      <template #item.description="{ item }">
        <p class="truncated">
          {{ item.description ? item.description : EMPTY_FIELD_SIGN }}
        </p>
      </template>

      <template #no-data> {{ noDataLabel ? noDataLabel : $t('NoData') }}</template>
    </component>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/mixins';

$border-radius: 2px;

.overviewTable {
  &:deep(.v-data-table) {
    background: unset;

    table {
      border-collapse: collapse;

      thead {
        background-color: rgb(var(--v-theme-off-white));
        @include mixins.elevation(4);
      }

      tr {
        border-radius: $border-radius;
        background-color: rgba(var(--v-theme-off-white), 70%);
        @include mixins.elevation(2);

        &:hover {
          background-color: rgb(var(--v-theme-off-white));
          @include mixins.elevation(4);
        }

        & + tr {
          border-top: 4px solid transparent;
        }

        th {
          @include mixins.typography('body-2');
          font-weight: 700 !important;
          @include mixins.pt(2);
          @include mixins.pb(2);
          color: rgb(var(--v-theme-on-surface));
          vertical-align: middle;
          // Prevent horizontal sticking when fixedHeader is enabled
          left: auto !important;
          right: auto !important;
          &:last-child {
            &.contextMenu {
              padding: 0 !important;
            }
          }
        }

        .truncated {
          line-clamp: 3;
          -webkit-line-clamp: 3;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        td {
          @include mixins.pt(2);
          @include mixins.pb(2);
          @include mixins.typography('body-2');
          font-weight: 500 !important;
          color: rgb(var(--v-theme-on-surface));
          vertical-align: middle;
          &:last-child {
            @include mixins.pr(2);
          }
          &:has(.contextMenuButton) {
            padding-right: 8px;
            padding-left: 8px;
          }
        }

        td:first-child,
        th:first-child {
          border-top-left-radius: $border-radius !important;
          border-bottom-left-radius: $border-radius !important;
        }

        td:last-child,
        th:last-child {
          border-top-right-radius: $border-radius !important;
          border-bottom-right-radius: $border-radius !important;
        }
      }
    }

    .v-table {
      &.table-plain {
        @include mixins.table-base;

        table {
          thead tr th,
          tbody tr th {
            @include mixins.bordered-cells;
          }
          tbody tr td {
            @include mixins.bordered-cells;

            &.nested-table {
              padding: 0 !important;
              table {
                tbody tr {
                  td {
                    &:first-child {
                      border-top: none;
                      border-left: none;
                    }
                    &:last-child {
                      border-right: none;
                    }
                  }
                  &:last-child {
                    td {
                      border-bottom: none;
                    }
                  }
                }
              }
            }
          }
        }
      }
      &.table-no-borders {
        @include mixins.table-base;
        table {
          thead tr,
          tbody tr {
            @include mixins.mb(2);
            & th,
            td {
              padding: 2px 8px !important;
              border: none !important;
              margin-bottom: 0 !important;
              height: auto !important;
              vertical-align: top !important;
              &:first-child {
                padding-left: 0 !important;
              }
              &:last-child {
                padding-right: 0 !important;
              }

              &.nested-table {
                padding: 0 !important;
                table {
                  tbody tr td,
                  tbody tr th {
                    padding: 2px 8px !important;
                    border: none !important;
                    margin-bottom: 0 !important;
                    height: auto !important;
                    &:first-child {
                      padding-left: 0 !important;
                    }
                    &:last-child {
                      padding-right: 0 !important;
                    }
                  }
                }
              }
            }
          }
        }
      }
      &.table-fit-content {
        width: fit-content;
        .v-table__wrapper {
          width: fit-content;
          table {
            width: auto;
          }
        }
      }
    }
  }
  &.zebra {
    &:deep(.v-data-table) {
      tr {
        @include mixins.elevation(0);
      }
      tr:nth-child(odd) {
        td {
          background-color: rgb(var(--v-theme-secondary-lighten-5), 20%);
        }
      }
      tr:nth-child(even) {
        td {
          background-color: rgb(var(--v-theme-secondary-lighten-5), 70%);
        }
      }
      tr:hover {
        @include mixins.elevation(0);
      }
    }
  }
  &.no-spacing {
    &:deep(.v-data-table) {
      padding: 0 !important;
      border-radius: 0 !important;
      @include mixins.elevation(3);
      tr {
        td,
        th {
          border-left: 1px solid rgb(var(--v-theme-primary-lighten-4));
        }
        td:first-child,
        th:first-child {
          border-radius: 0 !important;
        }
      }

      table {
        border-collapse: collapse;
      }
    }
  }
}
</style>
